import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

const initialState = {
  cartItems: localStorage.getItem('cartItems')
    ? JSON.parse(localStorage.getItem('cartItems'))
    : { products: [], customproducts: [] },
  cartTotalQuantity: 0,
  cartTotalAmount: 0,
  cartTotalShip: 0,
  customQuantity: 0,
  customTotal: 0,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart(state, action) {
      const existingIndex = state?.cartItems?.products?.findIndex(
        (item) =>
          item._id === action.payload.products._id && Number(item.new_price) === Number(action.payload.new_price)
      );
      if (existingIndex >= 0) {
        state.cartItems.products[existingIndex] = {
          ...state.cartItems?.products[existingIndex],
          cartQuantity: state.cartItems?.products[existingIndex].cartQuantity + action.payload.cartQty,
        };
        // toast.info("Increased product quantity", {
        //   position: "bottom-left",
        // });
      } else {
        const tempProductItem = {
          ...action.payload.products,
          cartQuantity: action.payload.cartQty,
          new_price: action.payload.new_price,
          old_price: action.payload.old_price,
          discount: action.payload.discount,
          selectedSize: action.payload.selectedSize,
        };
        state?.cartItems?.products?.push(tempProductItem);
        toast.success('Product added to cart', {
          position: 'bottom-left',
        });
      }
      localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
    },
    customProductAddToCart(state, action) {
      const tempProductItem = {
        ...action.payload.customproducts,
        cartQuantity: action.payload.cartQty,
      };
      state?.cartItems?.customproducts?.push(tempProductItem);
      toast.success('Custom Product added to cart', {
        position: 'bottom-left',
      });
      localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
    },
    decreaseCart(state, action) {
      const itemIndex = state.cartItems.products.findIndex(
        (item) => item._id === action.payload._id && Number(item.new_price) === Number(action.payload.new_price)
      );

      // Ensure the item exists
      if (itemIndex !== -1) {
        const item = state.cartItems.products[itemIndex];

        if (item.cartQuantity > 1) {
          // Decrease quantity
          item.cartQuantity -= 1;

          toast.info('Decreased product quantity', {
            position: 'bottom-left',
          });
        } else {
          // Remove item from cart when quantity is 1
          state.cartItems.products = state.cartItems.products.filter(
            (item) => !(item._id === action.payload._id && Number(item.new_price) === Number(action.payload.new_price))
          );

          toast.error('Product removed from cart', {
            position: 'bottom-left',
          });
        }

        // Update localStorage only if the cart changes
        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      }
    },
    cartInputQuantity(state, action) {
      const productIndex = state.cartItems.products.findIndex(
        (item) =>
          item._id === action.payload.products._id && Number(item.new_price) === Number(action.payload.new_price)
      );

      if (productIndex >= 0) {
        // Update the quantity of the existing product
        state.cartItems.products[productIndex] = {
          ...state.cartItems.products[productIndex],
          cartQuantity: action.payload.cartQty,
        };

        toast.info('Updated product quantity', {
          position: 'bottom-left',
        });
      } else {
        // Add the new product to the cart
        const newProduct = {
          ...action.payload.products,
          cartQuantity: action.payload.cartQty,
        };

        state.cartItems.products.push(newProduct);

        toast.success('Product added to cart', {
          position: 'bottom-left',
        });
      }

      // Update localStorage only after changes
      localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
    },
    removeFromCart(state, action) {
      const filteredCartItems = state.cartItems.products.filter(
        (item) => item._id !== action.payload._id || Number(item.new_price) !== Number(action.payload.new_price)
      );

      if (filteredCartItems.length !== state.cartItems.products.length) {
        state.cartItems.products = filteredCartItems;

        toast.error('Product removed from cart', {
          position: 'bottom-left',
        });

        // Update localStorage only when the cart changes
        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      }
    },
    removeCustomProductFromCart(state, action) {
      if (action.payload !== -1) {
        state.cartItems.customproducts.splice(action.payload, 1);

        toast.error('Custom Product removed from cart', {
          position: 'bottom-left',
        });

        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      }

      return state;
    },
    getTotals(state, action) {
      const productsArray = JSON.parse(JSON.stringify(state?.cartItems?.products || []));
      const customProductsArray = JSON.parse(JSON.stringify(state?.cartItems?.customproducts || []));
      const { total, quantity } = productsArray.reduce(
        (cartTotal, cartItem) => {
          const products = cartItem;
          if (products) {
            const { cartQuantity } = products;
            const itemTotal = Number(products?.new_price) * Number(cartQuantity);

            cartTotal.total += itemTotal;
            cartTotal.quantity += Number(cartQuantity);
          }
          return cartTotal;
        },
        {
          total: 0,
          quantity: 0,
        }
      );

      const { customProductTotal, customProductQuantity } = customProductsArray.reduce(
        (cartTotal, cartItem) => {
          const products = cartItem;
          if (products) {
            const { cartQuantity } = products;
            const itemTotal = Number(products?.totalPrice) * Number(cartQuantity);

            cartTotal.customProductTotal += itemTotal;
            cartTotal.customProductQuantity += Number(cartQuantity);
          }
          return cartTotal;
        },
        {
          customProductTotal: 0,
          customProductQuantity: 0,
        }
      );

      const roundedTotal = parseFloat(total.toFixed(2));
      const roundedCustomTotal = parseFloat(customProductTotal.toFixed(2));
      state.cartTotalQuantity = quantity;
      state.cartTotalAmount = roundedTotal;
      state.customQuantity = customProductQuantity;
      state.customTotal = roundedCustomTotal;
    },

    clearCart(state, action) {
      state.cartItems = { products: [], customproducts: [] };
      localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      toast.error('Cart cleared', { position: 'bottom-left' });
    },
  },
});

export const {
  addToCart,
  customProductAddToCart,
  decreaseCart,
  removeFromCart,
  removeCustomProductFromCart,
  getTotals,
  clearCart,
  cartInputQuantity,
} = cartSlice.actions;

export default cartSlice.reducer;
